import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";

import { ensurePdfWorkerConfigured } from "@/features/document-viewport/pdf-worker";
import type {
  DocumentSource,
  PageMetrics,
  ViewportRenderState,
} from "@/features/document-viewport/types";
import { getRenderScaleForZoom } from "@/features/document-viewport/utils";

interface PdfViewportProps {
  source: DocumentSource;
  activePage: number;
  baseWidth: number;
  baseHeight: number;
  zoom: number;
  activePageMetrics: PageMetrics | null;
  onPageCountChange: (pageCount: number) => void;
  onPageMetricsChange: (metrics: PageMetrics) => void;
  onPageStatusChange: (
    pageNumber: number,
    status: ViewportRenderState,
    errorMessage?: string
  ) => void;
  onRenderCommit: () => void;
}

type CanvasSlot = "front" | "back";

interface RasterState {
  pageNumber: number | null;
  scale: number;
}

const EMPTY_RASTER: RasterState = {
  pageNumber: null,
  scale: 1,
};
// Hard cap for high-zoom clarity; device budgets below reduce this on constrained clients.
const MAX_EFFECTIVE_RASTER_SCALE = 8;
// 64M pixels is roughly 244 MiB per canvas at RGBA; double buffering can briefly double that.
const MAX_RASTER_PIXELS = 64_000_000;
// Keep either canvas dimension safely below common browser/GPU texture limits.
const MAX_RASTER_DIMENSION = 8_192;

function getPageMetrics(pageNumber: number, viewport: pdfjs.PageViewport): PageMetrics {
  return {
    pageNumber,
    width: viewport.width,
    height: viewport.height,
    // The viewport frame derives height from a fitted width, so aspectRatio is height / width.
    aspectRatio: viewport.height / viewport.width,
    rotation: viewport.rotation,
  };
}

function getTargetRasterScale(args: {
  zoom: number;
  visibleRaster: RasterState;
  activePage: number;
}) {
  const { zoom, visibleRaster, activePage } = args;
  const quantizedScale = getRenderScaleForZoom(zoom);

  if (visibleRaster.pageNumber !== activePage) {
    return quantizedScale;
  }

  const currentScale = visibleRaster.scale;
  if (quantizedScale >= currentScale) {
    return quantizedScale;
  }

  const downgradeThreshold = currentScale - 0.4;
  return zoom <= downgradeThreshold ? quantizedScale : currentScale;
}

function getCanvasPixelRatio() {
  if (typeof window === "undefined") return 1;
  return Math.max(1, window.devicePixelRatio || 1);
}

function getRasterBudget() {
  if (typeof window === "undefined") {
    return {
      maxEffectiveScale: 4,
      maxPixels: 32_000_000,
      maxDimension: 6_144,
    };
  }

  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  if (isMobile || (memory !== undefined && memory <= 4)) {
    return {
      maxEffectiveScale: memory !== undefined && memory <= 2 ? 4 : 6,
      maxPixels: memory !== undefined && memory <= 2 ? 16_000_000 : 32_000_000,
      maxDimension: memory !== undefined && memory <= 2 ? 4_096 : 6_144,
    };
  }

  return {
    maxEffectiveScale: MAX_EFFECTIVE_RASTER_SCALE,
    maxPixels: MAX_RASTER_PIXELS,
    maxDimension: MAX_RASTER_DIMENSION,
  };
}

function getEffectiveRasterScale(args: {
  targetRasterScale: number;
  baseWidth: number;
  baseHeight: number;
}) {
  const { targetRasterScale, baseWidth, baseHeight } = args;
  const budget = getRasterBudget();
  const desiredScale = Math.min(
    targetRasterScale * getCanvasPixelRatio(),
    budget.maxEffectiveScale,
    MAX_EFFECTIVE_RASTER_SCALE
  );
  const pixelBudgetScale = Math.sqrt(
    budget.maxPixels / Math.max(1, baseWidth * baseHeight)
  );
  const dimensionScale = Math.min(
    budget.maxDimension / Math.max(1, baseWidth),
    budget.maxDimension / Math.max(1, baseHeight)
  );

  return Math.max(1, Math.min(desiredScale, pixelBudgetScale, dimensionScale));
}

export function PdfViewport({
  source,
  activePage,
  baseWidth,
  baseHeight,
  zoom,
  activePageMetrics,
  onPageCountChange,
  onPageMetricsChange,
  onPageStatusChange,
  onRenderCommit,
}: PdfViewportProps) {
  const frontCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const backCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const documentRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const pageRef = useRef<pdfjs.PDFPageProxy | null>(null);
  const renderTaskRef = useRef<pdfjs.RenderTask | null>(null);
  const destroyedTaskRef = useRef<Promise<void>>(Promise.resolve());
  const requestIdRef = useRef(0);
  const loadRequestIdRef = useRef(0);
  const metricsRequestIdRef = useRef(0);
  const lastRevisionRef = useRef(source.revisionId);
  const lastObservedFileUrlRef = useRef(source.fileUrl);
  const [loadSourceUrl, setLoadSourceUrl] = useState(source.fileUrl);
  const [loadVersion, setLoadVersion] = useState(1);
  const [documentLoadEpoch, setDocumentLoadEpoch] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [visibleSlot, setVisibleSlot] = useState<CanvasSlot>("front");
  const [slotRasters, setSlotRasters] = useState<Record<CanvasSlot, RasterState>>({
    front: EMPTY_RASTER,
    back: EMPTY_RASTER,
  });

  const visibleRaster = slotRasters[visibleSlot];

  const canvases = useMemo(
    () => ({
      front: frontCanvasRef,
      back: backCanvasRef,
    }),
    []
  );

  useEffect(() => {
    if (lastRevisionRef.current !== source.revisionId) {
      lastRevisionRef.current = source.revisionId;
      lastObservedFileUrlRef.current = source.fileUrl;
      setLoadSourceUrl(source.fileUrl);
      setLoadVersion((current) => current + 1);
      return;
    }

    if (lastObservedFileUrlRef.current !== source.fileUrl) {
      lastObservedFileUrlRef.current = source.fileUrl;
      if (!documentLoaded) {
        setLoadSourceUrl(source.fileUrl);
        setLoadVersion((current) => current + 1);
      }
    }
  }, [documentLoaded, source.fileUrl, source.revisionId]);

  useEffect(() => {
    const loadRequestId = ++loadRequestIdRef.current;
    let cancelled = false;
    let loadingTask: pdfjs.PDFDocumentLoadingTask | null = null;
    let destruction: Promise<void> | null = null;

    const destroyLoadingTask = () => {
      if (loadingTask && !destruction) {
        destruction = loadingTask.destroy().catch(() => {});
        destroyedTaskRef.current = destruction;
      }
      return destruction;
    };

    const loadPdf = async () => {
      try {
        await ensurePdfWorkerConfigured();
        if (cancelled || loadRequestId !== loadRequestIdRef.current) {
          return;
        }

        setDocumentLoaded(false);
        setVisibleSlot("front");
        setSlotRasters({
          front: EMPTY_RASTER,
          back: EMPTY_RASTER,
        });

        await destroyedTaskRef.current;

        if (cancelled || loadRequestId !== loadRequestIdRef.current) {
          return;
        }
        setErrorMessage(null);

        loadingTask = pdfjs.getDocument({
          url: loadSourceUrl,
          withCredentials: false,
        });

        const pdfDocument = await loadingTask.promise;
        if (cancelled || loadRequestId !== loadRequestIdRef.current) {
          return;
        }

        documentRef.current = pdfDocument;
        pageRef.current?.cleanup();
        pageRef.current = null;
        onPageStatusChange(1, "loading");

        const page = await pdfDocument.getPage(1);
        if (cancelled || loadRequestId !== loadRequestIdRef.current) {
          page.cleanup();
          return;
        }
        const viewport = page.getViewport({ scale: 1 });
        onPageMetricsChange(getPageMetrics(1, viewport));
        page.cleanup();

        onPageCountChange(pdfDocument.numPages || 1);
        setDocumentLoaded(true);
        setDocumentLoadEpoch(loadRequestId);
        setErrorMessage(null);
      } catch (error: unknown) {
        if (cancelled || loadRequestId !== loadRequestIdRef.current) {
          return;
        }

        documentRef.current = null;
        const message = error instanceof Error ? error.message : "Failed to load PDF";
        setErrorMessage(message);
        setDocumentLoaded(false);
        onPageStatusChange(activePage, "error", message);
        void destroyLoadingTask();
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      pageRef.current?.cleanup();
      pageRef.current = null;
      setDocumentLoaded(false);
      documentRef.current = null;
      void destroyLoadingTask();
    };
  }, [loadSourceUrl, loadVersion, onPageCountChange, onPageMetricsChange, onPageStatusChange]);

  useEffect(() => {
    if (!documentLoaded) {
      return;
    }

    const metricsRequestId = ++metricsRequestIdRef.current;
    const pdfDocument = documentRef.current;
    if (!pdfDocument) {
      return;
    }

    onPageStatusChange(activePage, "loading");

    const loadMetrics = async () => {
      let page: pdfjs.PDFPageProxy | null = null;

      try {
        if (activePage < 1 || activePage > pdfDocument.numPages) {
          return;
        }

        page = await pdfDocument.getPage(activePage);
        if (metricsRequestId !== metricsRequestIdRef.current) {
          return;
        }

        const viewport = page.getViewport({ scale: 1 });
        if (metricsRequestId !== metricsRequestIdRef.current) {
          return;
        }

        onPageMetricsChange(getPageMetrics(activePage, viewport));
      } catch (error: unknown) {
        if (metricsRequestId !== metricsRequestIdRef.current) {
          return;
        }

        const message =
          error instanceof Error ? error.message : "Failed to load PDF page metrics";
        setErrorMessage(message);
        onPageStatusChange(activePage, "error", message);
      } finally {
        page?.cleanup();
      }
    };

    void loadMetrics();
  }, [activePage, documentLoadEpoch, documentLoaded, onPageMetricsChange, onPageStatusChange]);

  useEffect(() => {
    const pdfDocument = documentRef.current;
    const pageMetrics = activePageMetrics;

    if (!pdfDocument || !pageMetrics || baseWidth <= 0 || baseHeight <= 0) {
      return;
    }

    const targetRasterScale = getTargetRasterScale({
      zoom,
      visibleRaster,
      activePage,
    });
    const visibleRasterMatchesTarget =
      visibleRaster.pageNumber === activePage &&
      Math.abs(visibleRaster.scale - targetRasterScale) < 0.001;

    if (visibleRasterMatchesTarget) {
      return;
    }

    const stagingSlot: CanvasSlot = visibleSlot === "front" ? "back" : "front";
    const stagingCanvas = canvases[stagingSlot].current;
    if (!stagingCanvas) {
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    const scheduleDelay = visibleRaster.pageNumber === activePage ? 120 : 0;
    onPageStatusChange(activePage, "rendering");

    const timeout = window.setTimeout(() => {
      void pdfDocument
        .getPage(activePage)
        .then((page) => {
          if (requestIdRef.current !== currentRequestId) {
            page.cleanup();
            onPageStatusChange(activePage, "cancelled");
            return;
          }

          renderTaskRef.current?.cancel();
          pageRef.current?.cleanup();

          const effectiveRasterScale = getEffectiveRasterScale({
            targetRasterScale,
            baseWidth,
            baseHeight,
          });
          const renderPixelWidth = Math.max(1, Math.round(baseWidth * effectiveRasterScale));
          const renderScale = renderPixelWidth / pageMetrics.width;
          const viewport = page.getViewport({ scale: renderScale });
          const context = stagingCanvas.getContext("2d", { alpha: false });

          if (!context) {
            const message = "Canvas 2D context unavailable";
            setErrorMessage(message);
            onPageStatusChange(activePage, "error", message);
            page.cleanup();
            return;
          }

          stagingCanvas.width = Math.max(1, Math.round(viewport.width));
          stagingCanvas.height = Math.max(1, Math.round(viewport.height));
          // Percentage CSS size tracks the host baseWidth/baseHeight container on
          // workspace resize without requiring a re-raster at the same zoom level.
          stagingCanvas.style.width = `${targetRasterScale * 100}%`;
          stagingCanvas.style.height = `${targetRasterScale * 100}%`;
          stagingCanvas.style.visibility = "hidden";
          context.clearRect(0, 0, stagingCanvas.width, stagingCanvas.height);

          const renderTask = page.render({
            canvas: stagingCanvas,
            canvasContext: context,
            viewport,
            background: "rgb(255, 255, 255)",
          });

          renderTaskRef.current = renderTask;

          return renderTask.promise
            .then(() => {
              if (requestIdRef.current !== currentRequestId) {
                page.cleanup();
                onPageStatusChange(activePage, "cancelled");
                return;
              }

              pageRef.current = page;
              stagingCanvas.style.visibility = "";
              setSlotRasters((current) => ({
                ...current,
                [stagingSlot]: {
                  pageNumber: activePage,
                  scale: targetRasterScale,
                },
              }));
              setVisibleSlot(stagingSlot);
              setErrorMessage(null);
              onPageStatusChange(activePage, "ready");
              onRenderCommit();
            })
            .catch((error: unknown) => {
              page.cleanup();
              if (
                error &&
                typeof error === "object" &&
                "name" in error &&
                error.name === "RenderingCancelledException"
              ) {
                onPageStatusChange(activePage, "cancelled");
                return;
              }

              const message =
                error instanceof Error ? error.message : "Failed to render PDF page";
              setErrorMessage(message);
              onPageStatusChange(activePage, "error", message);
            });
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : "Failed to load PDF page";
          setErrorMessage(message);
          onPageStatusChange(activePage, "error", message);
        });
    }, scheduleDelay);

    return () => {
      window.clearTimeout(timeout);
      renderTaskRef.current?.cancel();
    };
  }, [
    activePage,
    activePageMetrics,
    baseHeight,
    baseWidth,
    canvases,
    documentLoadEpoch,
    onPageStatusChange,
    onRenderCommit,
    visibleRaster,
    visibleSlot,
    zoom,
  ]);

  const canvasSlots: CanvasSlot[] = ["front", "back"];

  return (
    <div
      className="relative"
      style={{
        width: `${baseWidth}px`,
        height: `${baseHeight}px`,
      }}
    >
      {canvasSlots.map((slot) => {
        const slotRaster = slotRasters[slot];
        const isVisible = slot === visibleSlot && slotRaster.pageNumber === activePage;
        const hasRaster = slotRaster.pageNumber !== null;

        return (
          <canvas
            key={slot}
            ref={slot === "front" ? frontCanvasRef : backCanvasRef}
            className="absolute left-0 top-0 block rounded-md border bg-white shadow-sm transition-opacity duration-75"
            style={{
              opacity: isVisible ? 1 : 0,
              pointerEvents: "none",
              width: hasRaster ? `${slotRaster.scale * 100}%` : undefined,
              height: hasRaster ? `${slotRaster.scale * 100}%` : undefined,
              transform: `scale(${zoom / slotRaster.scale})`,
              transformOrigin: "top left",
              display: errorMessage ? "none" : "block",
            }}
          />
        );
      })}
      {errorMessage ? (
        <div className="flex h-24 w-full items-center justify-center rounded-md border border-destructive/40 bg-destructive/5 px-3 text-sm text-destructive">
          Failed to load PDF: {errorMessage}
        </div>
      ) : null}
    </div>
  );
}
